import { ChangeEvent, useEffect, useRef, useState } from 'react'

import { AiOutlineSearch } from 'react-icons/ai'
import { TERMS } from '../../utils/data/terms.data'

import {
    process_course_name,
    process_search_string,
    standardize_course_name
} from '../../utils/functions/course_name_util'

import { useCourseDataContext } from '../../utils/hooks/useCourseDataContext'
import { useSchool } from '../../utils/hooks/useSchool'
import type { ICourseData, ITerm } from '../../utils/interfaces/interfaces'
import CourseCard from './CourseCard/CourseCard.component'
import { AddRequestCard } from './UtilCard/UtilCard.component'

const Dashboard = () => {
    const school = useSchool()
    // context & state hooks
    const [courses] = useCourseDataContext()
    const [search_string, set_search_string] = useState('')
    const [raw_input, set_raw_input] = useState('')
    const isComposing = useRef(false)
    const [courses_this_term, set_courses_this_term] = useState<
        Array<ICourseData>
    >([])
    const [displayed_courses, set_displayed_courses] = useState<
        Array<ICourseData>
    >([])

    const [selected_term, set_selected_term] = useState<ITerm>(TERMS[0])

    // effect hooks

    // filter courses when selecting new term / category
    useEffect(() => {
        set_courses_this_term(
            courses.filter(({ school_name_and_term }) => {
                return (
                    school_name_and_term.toLowerCase() ===
                    selected_term.school_name_and_term.toLowerCase()
                )
            })
        )
    }, [courses, selected_term])

    // filter term when search_string is updated (i.e. user typing in input)
    useEffect(() => {
        set_displayed_courses(
            courses_this_term
                // Hide alias courses — only show canonical (the canonical card displays alias names)
                .filter((course: ICourseData) => !course.canonical_course)
                .filter(({ course_id, course_name, cross_listed_as }: ICourseData) => {
                    const processedCourseName = process_course_name(course_name);
                    const cleanedSearchString = standardize_course_name(search_string);
                    const fullyProcessedSearchString = process_search_string(search_string);

                    // Also match against cross-listed alias names
                    const aliasMatch = cross_listed_as?.some(alias =>
                        process_course_name(alias).includes(cleanedSearchString)
                    ) ?? false;

                    return (
                        processedCourseName.includes(cleanedSearchString) || processedCourseName.includes(fullyProcessedSearchString) || course_id.includes(search_string) || aliasMatch
                    )
                })
                .sort((course1, course2) => {
                    return (
                        parseInt(
                            (course1.course_name.match(/\d+/) ?? [
                                course1.course_id
                            ])[0]
                        ) -
                        parseInt(
                            (course2.course_name.match(/\d+/) ?? [
                                course2.course_id
                            ])[0]
                        )
                    )
                })
            // .splice(0, 11)
        )
    }, [courses_this_term, search_string])

    return (
        <div id="dashboard" className="w-full mx-auto max-w-3xl">
            {/* Search Bar */}

            <div
                id="search-bar-container"
                className={`mt-32 w-full px-2 gap-2 flex items-center border-solid border-b-2 text-xl border-b-gray-5 ${school.classes.hoverBorderB} ${school.classes.focusBorderB} focus:border-solid focus:border-b-2`}
            >
                <input
                    id="search-bar"
                    className="outline-0 inline-block relative  bg-transparent flex-grow"
                    placeholder="查找课程/课号"
                    onCompositionStart={() => { isComposing.current = true }}
                    onCompositionEnd={(event) => {
                        isComposing.current = false
                        const val = (event.target as HTMLInputElement).value
                        set_raw_input(val)
                        set_search_string(val.toLowerCase())
                    }}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                        const val = event.target.value
                        set_raw_input(val)
                        if (!isComposing.current) {
                            set_search_string(val.toLowerCase())
                        }
                    }}
                    value={raw_input}
                />
                <AiOutlineSearch className="flex-grow-0 font-bold" />
            </div>

            {/* Terms / Categories Bar */}
            <div
                id="filter-bar"
                className="flex relative w-full text-center my-8 mx-auto flex-wrap gap-4 justify-around"
            >
                {TERMS.filter(term => !term.hidden).map(term => {
                    const selected =
                        term.school_name_and_term ===
                        selected_term.school_name_and_term

                    return (
                        <button
                            className={`font-medium transition-background duration-150 w-40 p-1 rounded-2xl border-2 border-solid ${school.classes.border} text-center ${
                                selected
                                    ? `${school.classes.bg} text-white`
                                    : `bg-transparent ${school.classes.text} ${school.classes.hoverBg} hover:opacity-75 hover:text-white`
                            }`}
                            key={term.school_name_and_term}
                            onClick={() => set_selected_term(term)}
                        >
                            {term.label}
                        </button>
                    )
                })}
            </div>

            {/* Actual Courses */}

            <div className="flex my-5 mx-auto gap-8 mb-10 flex-row flex-wrap justify-around content-start">
                {displayed_courses.map(course => {
                    return (
                        <CourseCard
                            key={course.course_name}
                            course={course}
                            school={school}
                        />
                    )
                })}
                {courses_this_term.length > 0 && <AddRequestCard />}
            </div>
        </div>
    )
}

export default Dashboard
